import { Link } from "react-router-dom";
import { BookOpen, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import BlogCard from "@/components/blog/BlogCard";
import { SkeletonBlogCard, SkeletonGrid } from "@/components/ui/skeleton-premium";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import type { BlogPost } from "@/hooks/useBlogPosts";

interface BlogFeedSectionProps {
  title?: string;
  subtitle?: string;
  categorySlugs?: string[];
  limit?: number;
  ctaLink?: string;
  ctaText?: string;
  showFeatured?: boolean;
}

const BlogFeedSection = ({
  title = "Latest Insights",
  subtitle = "Expert tips and strategies to grow your business",
  categorySlugs = [],
  limit = 3,
  ctaLink = "/blog",
  ctaText = "View All Articles",
  showFeatured = false,
}: BlogFeedSectionProps) => {
  const { data: posts, isLoading } = useQuery({
    queryKey: ["blog-feed", categorySlugs, limit, showFeatured],
    queryFn: async () => {
      // If specific categories requested
      if (categorySlugs.length > 0) {
        const { data: categories } = await supabase
          .from("blog_categories")
          .select("id")
          .in("slug", categorySlugs);

        if (categories && categories.length > 0) {
          const categoryIds = categories.map((c) => c.id);

          const { data, error } = await supabase
            .from("blog_posts")
            .select(`*, category:blog_categories(*)`)
            .eq("published", true)
            .in("category_id", categoryIds)
            .order("published_at", { ascending: false })
            .limit(limit);

          if (error) throw error;
          if (data && data.length > 0) return data as BlogPost[];
        }
      }

      // Fallback: featured or latest posts
      let query = supabase
        .from("blog_posts")
        .select(`*, category:blog_categories(*)`)
        .eq("published", true);

      if (showFeatured) {
        query = query.eq("featured", true);
      }

      const { data, error } = await query
        .order("published_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as BlogPost[];
    },
  });

  const { ref, isInView } = useScrollAnimation({ threshold: 0.1, triggerOnce: true });

  // Show empty state placeholder instead of null
  if (!isLoading && (!posts || posts.length === 0)) {
    return (
      <section ref={ref} className="py-16 md:py-24 bg-muted/30">
        <div className="container">
          <div className={cn(
            "text-center mb-12 transition-all duration-700",
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}>
            <div className="inline-flex items-center gap-2 text-primary mb-4">
              <BookOpen className="h-5 w-5" />
              <span className="text-sm font-medium uppercase tracking-wider">
                Resources
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{title}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {subtitle}
            </p>
          </div>
          
          {/* Empty state placeholder */}
          <div className={cn(
            "text-center py-12 px-6 rounded-2xl bg-card border border-border/50 transition-all duration-700",
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
          style={{ transitionDelay: "200ms" }}
          >
            <BookOpen className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Fresh Content Coming Soon
            </h3>
            <p className="text-muted-foreground max-w-md mx-auto mb-6">
              We're working on insightful articles to help your business grow. Check back soon!
            </p>
            <Button asChild variant="outline">
              <Link to="/contact" className="inline-flex items-center gap-2">
                Get Notified
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="py-16 md:py-24 bg-muted/30">
      <div className="container">
        {/* Header */}
        <div className={cn(
          "text-center mb-12 transition-all duration-700",
          isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        )}>
          <div className="inline-flex items-center gap-2 text-primary mb-4">
            <BookOpen className="h-5 w-5" />
            <span className="text-sm font-medium uppercase tracking-wider">
              Resources
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{title}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Posts Grid */}
        {isLoading ? (
          <SkeletonGrid count={limit} columns="md:grid-cols-2 lg:grid-cols-3" className="gap-6">
            {(index, delay) => <SkeletonBlogCard key={index} delay={delay} />}
          </SkeletonGrid>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts?.map((post, index) => (
              <div
                key={post.id}
                className={cn(
                  "transition-all duration-500",
                  isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                )}
                style={{ transitionDelay: `${200 + index * 100}ms` }}
              >
                <BlogCard post={post} />
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className={cn(
          "text-center mt-10 transition-all duration-700",
          isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        )}
        style={{ transitionDelay: "500ms" }}
        >
          <Button asChild variant="outline" size="lg">
            <Link to={ctaLink} className="inline-flex items-center gap-2">
              {ctaText}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BlogFeedSection;
